import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Linking, Platform, ScrollView, StyleSheet, TouchableOpacity } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { LinearGradient } from "expo-linear-gradient";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { ErrorState } from "@/components/ui/ErrorState";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { authApi } from "@/lib/api/auth";
import { APP_URL, getStoreUrl } from "@/lib/appUrl";
import { LoadingState } from "@/components/ui/LoadingState";
import Logo from "@assets/logo.svg";
import { Modal } from "@/components/ui/Modal";
import { Pill } from "@/components/ui/Pill";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import QRCode from "react-native-qrcode-svg";
import { cn } from "@/lib/utils";
import { ImageBackground } from "@/components/ui/Image";

const INVITE_HERO_IMAGE = require("@assets/images/invite/invite-hero.png");

type CardStep = "invite" | "language-select";

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <View
      className={cn(
        "w-5 h-5 rounded-full border-2 items-center justify-center",
        selected ? "border-primary" : "border-stroke-50"
      )}
    >
      {selected && <View className="w-2.5 h-2.5 rounded-full bg-primary" />}
    </View>
  );
}

export default function InviteLandingScreen() {
  const router = useRouter();
  const { t, locale, setLocale, availableLocales, localeHydrated } = useI18n();
  const isWide = useIsWideScreen();

  const { token, locale: localeParam } = useLocalSearchParams<{
    token: string;
    locale?: string;
  }>();

  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [cardStep, setCardStep] = useState<CardStep>("invite");
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(locale);

  const decodedToken = token ? decodeURIComponent(token) : token;

  // Apply locale from URL param once, after storage hydration completes.
  // Falls back silently when the param is absent or not a recognised locale.
  const localeParamApplied = useRef(false);
  useEffect(() => {
    if (!localeHydrated || localeParamApplied.current) return;
    localeParamApplied.current = true;
    const isValid = availableLocales.some((l) => l.locale === localeParam);
    if (localeParam && isValid) {
      setLocale(localeParam as SupportedLocale);
    }
  }, [localeHydrated, localeParam, availableLocales, setLocale]);

  const tokenQuery = useQuery({
    queryKey: ["inviteToken", decodedToken],
    queryFn: () => authApi.verifyInviteToken(decodedToken!),

    enabled: Platform.OS === "web" && !!decodedToken,
    staleTime: Infinity,
    retry: false,
  });

  // ── Native: app is already installed, skip straight to register ───────────
  useEffect(() => {
    if (Platform.OS !== "web" && token) {
      router.replace({ pathname: "/(auth)/register", params: { token } });
    }
  }, [token, router]);

  const handleContinueOnWeb = useCallback(() => {
    if (!tokenQuery.data) return;
    router.replace({
      pathname: "/(auth)/register",
      params: { token: token!, email: tokenQuery.data.email },
    });
  }, [token, tokenQuery.data, router]);

  const handleDownloadApp = useCallback(async () => {
    if (isWide) {
      // Desktop: show QR code modal — encodes the invite URL so scanning on a
      // phone opens the app (if installed) or the mobile web landing page.
      setQrModalVisible(true);
      return;
    }
    // Mobile web: detect platform and open the correct store, with Android
    // referrer so the token survives through the install.
    const ua = typeof window !== "undefined" ? window.navigator.userAgent : "";
    const isAndroid = /android/i.test(ua);
    const url = getStoreUrl(isAndroid ? "android" : "ios", token);
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    }
  }, [isWide, token]);

  const handleSaveLanguage = useCallback(() => {
    setLocale(selectedLocale);
    setCardStep("invite");
  }, [selectedLocale, setLocale]);

  // ── Native: show nothing while redirecting ────────────────────────────────
  if (Platform.OS !== "web") return null;

  // ── No token ──────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <ErrorState
        testID="invite-landing-error"
        title="invite.landing.invalidLinkTitle"
        description="invite.landing.invalidLink"
      />
    );
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (tokenQuery.isPending) {
    return (
      <LoadingState
        mode="fullscreen"
        label="invite.landing.verifying"
        testID="invite-landing-loading"
      />
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (tokenQuery.isError) {
    return (
      <ErrorState
        testID="invite-landing-error"
        title="invite.landing.invalidLinkTitle"
        description="invite.landing.invalidLink"
      />
    );
  }

  // ── Verified: show invite landing ─────────────────────────────────────────
  return (
    <>
      <ScrollView
        className="flex-1 bg-white"
        contentContainerStyle={{ flexGrow: 1 }}
        testID="invite-landing-screen"
      >
        <ImageBackground
          source={INVITE_HERO_IMAGE}
          resizeMode="cover"
          style={{ flex: 1, width: "100%", height: "100%" }}
        >
          {/* White gradient overlay on mobile — fades from transparent to white */}
          {!isWide && (
            <LinearGradient
              colors={[
                "rgba(255,255,255,0)",
                "rgba(255,255,255,0)",
                "rgba(255,255,255,0.35)",
                "#FFFFFF",
              ]}
              locations={[0, 0, cardStep === "language-select" ? 0.45 : 0.6, 0.7]}
              style={StyleSheet.absoluteFill}
              pointerEvents="none"
            />
          )}
          <View
            className={
              isWide
                ? "flex-1 items-center justify-center px-page-x py-10"
                : "flex-1 items-center justify-end px-page-x py-20"
            }
          >
            <View
              className={
                isWide
                  ? "w-full max-w-lg items-center bg-white rounded-2xl p-8 shadow-lg"
                  : "w-full max-w-lg items-center"
              }
            >
              {/* ── Logo ─────────────────────────────────────────────────── */}
              <View className="mb-4">
                <Logo width={48} height={48} />
              </View>

              {cardStep === "language-select" ? (
                <>
                  {/* ── Welcome text ─────────────────────────────────────── */}
                  <AppText className="text-sm text-dark-text-15 text-center">
                    {t("vendor.languageSelect.welcomeTo")}
                  </AppText>
                  <AppText className="text-xl text-dark-text-50 text-center mb-6" weight="bold">
                    {t("vendor.languageSelect.appName")}
                  </AppText>

                  {/* ── Subtitle ─────────────────────────────────────────── */}
                  <AppText className="text-sm text-dark-text-30 text-center mb-6" weight="medium">
                    {t("vendor.languageSelect.title")}
                  </AppText>

                  {/* ── Language radio list ───────────────────────────────── */}
                  <View className="w-full mb-6 border rounded-lg border-stroke-50 bg-white">
                    {availableLocales.map((item, index) => (
                      <TouchableOpacity
                        key={item.locale}
                        onPress={() => setSelectedLocale(item.locale)}
                        accessibilityRole="radio"
                        accessibilityState={{
                          checked: selectedLocale === item.locale,
                        }}
                        accessibilityLabel={item.label}
                        testID={`language-option-${item.locale}`}
                        className={`flex-row items-center justify-between px-4 py-4 ${
                          index < availableLocales.length - 1 ? "border-b border-stroke-50" : ""
                        }`}
                      >
                        <AppText
                          weight="medium"
                          className={
                            selectedLocale === item.locale
                              ? "text-dark-text-50"
                              : "text-supporting-text"
                          }
                        >
                          {item.label}
                        </AppText>
                        <RadioDot selected={selectedLocale === item.locale} />
                      </TouchableOpacity>
                    ))}
                  </View>

                  {/* ── Save Preference button ───────────────────────────── */}
                  <Button
                    label="vendor.languageSelect.savePreference"
                    variant="primary"
                    onPress={handleSaveLanguage}
                    testID="invite-save-language"
                    className="w-full"
                  />
                </>
              ) : (
                <>
                  {/* ── Title ──────────────────────────────────────────── */}
                  <AppText className="text-xl text-dark-text-50 text-center mb-8" weight="bold">
                    {t("invite.landing.title")}
                  </AppText>

                  <View className="gap-4 w-full">
                    {/* ── Continue on website ──────────────────────────────────── */}
                    <Button
                      label="invite.landing.webOption.title"
                      variant="primary"
                      onPress={handleContinueOnWeb}
                      icon={<Icon as={Feather} name="globe" size={18} className="text-white" />}
                      testID="invite-continue-web"
                      className="w-full"
                    />

                    {/* ── "or" divider ─────────────────────────────────────────── */}
                    <View className="hidden tablet:flex flex-row items-center w-full">
                      <View className="flex-1 h-px bg-stroke-50" />
                      <AppText className="text-sm text-dark-text-10 mx-4">
                        {t("invite.landing.or")}
                      </AppText>
                      <View className="flex-1 h-px bg-stroke-50" />
                    </View>

                    {/* ── Download mobile app ──────────────────────────────────── */}
                    <Button
                      label={
                        isWide
                          ? "invite.landing.mobileOption.title"
                          : "invite.landing.mobileOption.titleShort"
                      }
                      variant="secondary"
                      onPress={handleDownloadApp}
                      icon={
                        <Icon as={Feather} name="smartphone" size={18} className="text-primary" />
                      }
                      testID="invite-download-app"
                      className="w-full"
                    />

                    {/* ── Hint text (desktop only) ─────────────────────────────── */}
                    {isWide && (
                      <AppText className="text-xs text-dark-text-10 text-center">
                        {t("invite.landing.mobileOption.hint")}
                      </AppText>
                    )}
                  </View>
                </>
              )}
            </View>
          </View>

          <Pill
            label="vendor.landing.changeLanguage"
            onPress={() => setCardStep("language-select")}
            className="absolute top-6 tablet:top-10 end-6 tablet:end-10"
            icon={<Icon as={Feather} name="globe" size={16} className="text-blue-50" />}
          />
        </ImageBackground>
      </ScrollView>

      {/* ── QR Code Modal (desktop web only) ───────────────────────────────── */}
      <Modal
        visible={qrModalVisible}
        onDismiss={() => setQrModalVisible(false)}
        useScrollView={false}
        contentProps={{ className: "items-center pt-0 lg:pt-0 pb-8 lg:pb-8" }}
      >
        <AppText className="text-lg text-dark-text-50 text-center mb-2" weight="bold">
          {t("invite.landing.qrModal.title")}
        </AppText>
        <AppText className="text-sm text-dark-text-15 text-center mb-6">
          {t("invite.landing.qrModal.subtitle")}
        </AppText>

        {/*
         * The QR encodes the full invite URL:
         *   {APP_URL}/invite/{token}
         *
         * APP_URL is derived from EXPO_PUBLIC_APP_URL (Infisical-managed in deployed
         * environments) and falls back to a localhost base URL in local/dev when
         * EXPO_PUBLIC_APP_URL is unset.
         * Scanning on a phone with the app installed → Universal Link / App Link
         * opens the register screen with the token directly.
         * Scanning without the app → mobile web landing page, token preserved.
         */}
        <QRCode value={`${APP_URL}/invite?token=${token}`} size={192} testID="invite-qr-code" />
      </Modal>
    </>
  );
}
